import { LOB } from "../../../api/routes";
import { status } from "../../../client";
import { KeysMatching } from "../../../util";

// These strings are saved in persisted data and can't be changed or you'll
// break saved filters.
export const SORT_FIELD_NAMES = [
  "company_name",
  "broker_name",
  "eff_date",
  "status",
  "tiv",
  "creation_date",
  "target_premium",
] as const;
export type SORT_FIELD_NAME = (typeof SORT_FIELD_NAMES)[number];

export type Filter = {
  setFields: (keyof Filter)[];
  selectedAssigneeUserIds: string[];
  selectedHandlerUserIds: string[];
  selectedStatuses: string[];
  selectedAppetites: string[];
  selectedIndications: string[];
  selectedBrokerTeams: string[];
  selectedLobs: LOB[];
  initialSortField: SORT_FIELD_NAME;
  initialSortOrder: "asc" | "desc";
  effectiveDateStart: string;
  effectiveDateEnd: string;
};

export const updatedFilter = <K extends keyof Filter>(
  field: K,
  value: Filter[K],
  filter: Filter
): Filter => {
  const isEmpty = value === "" || (Array.isArray(value) && value.length === 0);
  return {
    ...filter,
    [field]: value,
    setFields: isEmpty
      ? filter.setFields.filter((f) => f !== field)
      : Array.from(new Set(filter.setFields.concat(field))),
  };
};

export const defaultFilterForLobsAndArchived = (
  lobs: LOB[],
  statuses?: status[],
  assigneeIds?: string[]
): Filter => {
  const setFields: (keyof Filter)[] = ["selectedLobs"];
  if ((statuses?.length ?? 0) > 0) {
    setFields.push("selectedStatuses");
  }
  if ((assigneeIds?.length ?? 0) > 0) {
    setFields.push("selectedAssigneeUserIds");
  }
  return {
    setFields: setFields,
    selectedAssigneeUserIds: assigneeIds ?? [],
    selectedHandlerUserIds: [],
    selectedStatuses: statuses ?? [],
    selectedAppetites: [],
    selectedIndications: [],
    selectedBrokerTeams: [],
    initialSortField: "creation_date",
    initialSortOrder: "desc",
    selectedLobs: lobs,
    effectiveDateStart: "",
    effectiveDateEnd: "",
  };
};

export const DEFAULT_FILTER: Filter = {
  setFields: [],
  selectedAssigneeUserIds: [],
  selectedHandlerUserIds: [],
  selectedStatuses: [],
  selectedAppetites: [],
  selectedIndications: [],
  selectedBrokerTeams: [],
  initialSortField: "creation_date",
  initialSortOrder: "desc",
  selectedLobs: [],
  effectiveDateStart: "",
  effectiveDateEnd: "",
};

const KEY_MAPPING: { [key in keyof Filter]-?: string } = {
  setFields: "fields",
  selectedAssigneeUserIds: "assign",
  selectedHandlerUserIds: "handler",
  selectedStatuses: "st",
  selectedAppetites: "ap",
  selectedIndications: "ind",
  selectedBrokerTeams: "bt",
  initialSortField: "sf",
  initialSortOrder: "so",
  selectedLobs: "lobs",
  effectiveDateStart: "eds",
  effectiveDateEnd: "ede",
};

const REVERSE_KEY_MAPPING = Object.fromEntries(
  Object.entries(KEY_MAPPING).map(([a, b]) => [b, a])
) as { [key: string]: keyof Filter };

/* Returns a URL-safe serialization of a filter, suitable for use as a URL "search" */
export const serializeFilter = (filter: Filter): string => {
  const params = new URLSearchParams();
  for (const [untypedK, urlKey] of Object.entries(KEY_MAPPING)) {
    const k = untypedK as keyof Filter;

    const value = filter[k];
    if (k === "setFields" && Array.isArray(value)) {
      params.append(
        KEY_MAPPING.setFields,
        value
          .map((v) => KEY_MAPPING[v as keyof Filter])
          .filter((v) => !!v)
          .join(".")
      );
    } else if (filter["setFields"].includes(k)) {
      if (typeof value === "string") {
        params.append(urlKey, value);
      } else {
        for (const v of value) {
          if (v) {
            params.append(urlKey, v);
          }
        }
      }
    }
  }

  return params.toString();
};

/* Parse a filter from a URL search */
export const deserializeFilter = (search: string, userLobs: LOB[]): Filter => {
  const params = new URLSearchParams(search);

  const getOrDefault = (k: KeysMatching<Filter, string>): string =>
    params.get(KEY_MAPPING[k]) ?? defaultFilterForLobsAndArchived(userLobs)[k];

  const setFields = (params.get(KEY_MAPPING["setFields"])?.split(".") ?? [])
    .map((v) => REVERSE_KEY_MAPPING[v])
    .filter((v) => !!v);
  const selectedAssigneeUserIds = params.getAll(
    KEY_MAPPING["selectedAssigneeUserIds"]
  );
  const selectedHandlerUserIds = params.getAll(
    KEY_MAPPING["selectedHandlerUserIds"]
  );
  const selectedStatuses = params.getAll(KEY_MAPPING["selectedStatuses"]);
  const selectedAppetites = params.getAll(KEY_MAPPING["selectedAppetites"]);
  const selectedIndications = params.getAll(KEY_MAPPING["selectedIndications"]);
  const selectedBrokerTeams = params.getAll(KEY_MAPPING["selectedBrokerTeams"]);
  const selectedLobs = (params.getAll(KEY_MAPPING["selectedLobs"]) ||
    userLobs) as LOB[];
  const initialSortField = getOrDefault("initialSortField") as SORT_FIELD_NAME;
  const initialSortOrderString = getOrDefault("initialSortOrder");
  const initialSortOrder =
    initialSortOrderString === "asc" || initialSortOrderString == "desc"
      ? initialSortOrderString
      : DEFAULT_FILTER.initialSortOrder;
  const effectiveDateStart =
    params.get(KEY_MAPPING["effectiveDateStart"]) ?? "";
  const effectiveDateEnd = params.get(KEY_MAPPING["effectiveDateEnd"]) ?? "";

  return {
    setFields,
    selectedAssigneeUserIds,
    selectedHandlerUserIds,
    selectedStatuses,
    selectedAppetites,
    selectedIndications,
    selectedBrokerTeams,
    initialSortField,
    initialSortOrder,
    selectedLobs: selectedLobs,
    effectiveDateStart,
    effectiveDateEnd,
  };
};
